/**
 * NoticeSupplierConfirm — the D7 confirm ("which supplier is this letter
 * from?"). Lane 625, Phase 5 screen 2.
 *
 * Mounts inside the upload status card when the pipeline parks a classified
 * commercial notice at `awaiting_supplier` (Lane 618). The buyer picks, and the
 * pick posts to `POST /api/chat/upload/candidates/{id}/attach-supplier` — the
 * first and only moment a claim from that letter is attributed to a supplier.
 *
 * WHAT IS REUSED FROM THE SHIPPED CHAT PICKER
 * -------------------------------------------
 * `components/SupplierDisambiguation.js` (§5.EV) is the picker buyers already
 * know, and its BEHAVIOUR is the contract here:
 *   - the same endpoint, `GET /api/suppliers/search`, existing suppliers only
 *   - the same disambiguating hint, `$X.XM L12M · N materials`, rendered even
 *     when empty ("$0 L12M · 0 materials") so an absent value never reads as a
 *     silent omission — the seven same-prefix Parkers are unpickable by name
 *   - type-to-search, debounced, tap a row to choose
 *   - a dashed "special option" row for the non-supplier choice, the shape the
 *     portfolio-escape and material rows already use
 *   - it NEVER creates a supplier
 *
 * WHAT IS DELIBERATELY NOT REUSED, AND WHY
 * ----------------------------------------
 * 1. THE WIDGET. The chat picker uses `react-select`'s AsyncSelect with
 *    `menuPortalTarget={document.body}`. Both halves break here:
 *      - Home's palette is scoped to `.hm` (`--accent`, `--panel`, `--text`…
 *        are declared on that selector), so a menu portalled to `document.body`
 *        renders OUTSIDE the scope and loses every token.
 *      - Portalling exists in that component because a menu opening downward
 *        gets clipped. In this composer that risk is real and layered: `.hm`,
 *        `.hm-body`, `.hm-inner` and `.hm-chat` all set `overflow:hidden`, and
 *        the card sits directly above the input bar at the bottom of the
 *        column. This is the Lane 142 MetricInfo defect (a popover clipped
 *        invisible by an ancestor's overflow).
 *    So the results render as an IN-FLOW list instead. It cannot clip — the
 *    card grows and the pinned composer grows with it — and it inherits the
 *    page tokens because it is inside `.hm`.
 *
 * 2. SWALLOWING A FAILED SEARCH. The chat picker maps a failed request to an
 *    empty option list, so an outage looks identical to "no supplier matches".
 *    Here that is dangerous: "nothing matches" is the cue to choose "not a
 *    supplier on file", which records that this letter came from nobody we
 *    hold. A search outage must not be able to walk a buyer into that, so the
 *    two states are rendered differently. See `api/noticeAttach.js`.
 *
 * 3. TAP-TO-RESOLVE. The chat picker resolves the moment a row is tapped,
 *    because the cost of a wrong pick is a re-asked question. Here the cost is
 *    a stored claim attributed to a named supplier. It is recoverable (retract,
 *    then re-attach — B4's loop) but it is not free, so the pick is shown back
 *    to the buyer and committed by an explicit Confirm. RYAN: this is a
 *    judgment call, and one extra click on the main path — see the handover.
 *
 * NO REQUEST FIRES UNTIL THE BUYER TYPES. There is no seed load. The chat
 * picker seeds its menu with top-spend suppliers, but there those rows are
 * candidates the backend ranked FOR THAT QUERY. Here nothing about the letter
 * points at a supplier, so a list of the buyer's biggest suppliers would imply
 * a candidacy the pipeline never computed.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { searchSuppliers } from '../api/noticeAttach';
import {
  PICKER_COPY,
  confirmConsequence,
  formatHint,
  noMatchSentence,
  searchFailedSentence,
} from '../utils/noticeConfirmCopy';

/** Matches the chat picker's debounce. */
const SEARCH_DEBOUNCE_MS = 250;

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" /><line x1="20" y1="20" x2="16.5" y2="16.5" />
    </svg>
  );
}

export default function NoticeSupplierConfirm({
  claimsAvailable,
  authFetch,
  onConfirm,
  busy = false,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // `searchFailed` is tracked SEPARATELY from an empty result set on purpose —
  // see the module note. `null` = no search has run yet.
  const [searchFailed, setSearchFailed] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [chosen, setChosen] = useState(null);

  const mountedRef = useRef(true);
  // Monotonic request id: a slow earlier response must never overwrite a
  // faster later one (type "sk" then "skf" and the "sk" answer can land last).
  const seqRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const runSearch = useCallback(
    async (text) => {
      const seq = ++seqRef.current;
      setSearching(true);
      const { ok, results: rows } = await searchSuppliers(authFetch, text);
      // Stale response, or we unmounted while it was in flight.
      if (!mountedRef.current || seq !== seqRef.current) return;
      setSearching(false);
      setHasSearched(true);
      setSearchFailed(!ok);
      setResults(ok ? rows : []);
    },
    [authFetch],
  );

  const onQueryChange = (text) => {
    setQuery(text);
    if (timerRef.current) clearTimeout(timerRef.current);
    const trimmed = text.trim();
    if (!trimmed) {
      // Back to the resting state — and bump the sequence so an in-flight
      // response for a longer query cannot repopulate a cleared box.
      seqRef.current += 1;
      setResults([]);
      setSearching(false);
      setHasSearched(false);
      setSearchFailed(false);
      return;
    }
    timerRef.current = setTimeout(() => runSearch(trimmed), SEARCH_DEBOUNCE_MS);
  };

  // ── chosen: show the pick back before committing it ──────────────────────
  if (chosen) {
    const notOnFile = chosen.supplier_id === null;
    return (
      <div className="hm-nsc" data-testid="hm-nsc-confirm">
        <div className="hm-nsc-picked">
          <div className="hm-nsc-pickedname">
            {notOnFile ? PICKER_COPY.PICKED_NOT_ON_FILE : chosen.supplier_name}
          </div>
          <div className="hm-nsc-hint">
            {notOnFile
              ? PICKER_COPY.PICKED_NOT_ON_FILE_HINT
              : formatHint(chosen.l12m_spend, chosen.material_count)}
          </div>
        </div>
        <div className="hm-nsc-what">{confirmConsequence(notOnFile, claimsAvailable)}</div>
        <div className="hm-nsc-row">
          <button
            type="button"
            className="hm-nsc-go"
            disabled={busy}
            data-testid="hm-nsc-submit"
            onClick={() => onConfirm(chosen.supplier_id, notOnFile ? null : chosen.supplier_name)}
          >
            {busy ? PICKER_COPY.CONFIRMING : PICKER_COPY.CONFIRM}
          </button>
          <button
            type="button"
            className="hm-upact"
            disabled={busy}
            onClick={() => setChosen(null)}
          >
            {PICKER_COPY.CHANGE}
          </button>
        </div>
      </div>
    );
  }

  // ── picking ──────────────────────────────────────────────────────────────
  const trimmed = query.trim();
  return (
    <div className="hm-nsc" data-testid="hm-nsc">
      <div className="hm-nsc-field">
        <span className="hm-nsc-fieldicon"><SearchIcon /></span>
        <input
          type="text"
          className="hm-nsc-input"
          value={query}
          disabled={busy}
          placeholder={PICKER_COPY.SEARCH_PLACEHOLDER}
          aria-label={PICKER_COPY.SEARCH_LABEL}
          data-testid="hm-nsc-input"
          onChange={(e) => onQueryChange(e.target.value)}
        />
      </div>

      {searching && (
        <div className="hm-nsc-note" data-testid="hm-nsc-searching">
          {PICKER_COPY.SEARCHING}
        </div>
      )}

      {!searching && searchFailed && (
        // NOT "no matches". A failed search says so, because "nothing matches"
        // is the cue to record this letter as coming from nobody on file.
        <div className="hm-nsc-note bad" data-testid="hm-nsc-searchfailed">
          {searchFailedSentence(trimmed)}
        </div>
      )}

      {!searching && !searchFailed && hasSearched && results.length === 0 && (
        <div className="hm-nsc-note" data-testid="hm-nsc-nomatch">
          {noMatchSentence(trimmed)}
        </div>
      )}

      {!searching && !searchFailed && results.length > 0 && (
        <div role="listbox" aria-label="Matching suppliers" className="hm-nsc-list">
          {results.map((r) => (
            <button
              key={r.supplier_id}
              type="button"
              role="option"
              aria-selected="false"
              className="hm-nsc-opt"
              disabled={busy}
              data-testid="hm-nsc-opt"
              onClick={() => setChosen(r)}
            >
              <span className="hm-nsc-name">{r.supplier_name}</span>
              <span className="hm-nsc-hint">{formatHint(r.l12m_spend, r.material_count)}</span>
            </button>
          ))}
        </div>
      )}

      {/* design §1's first-class outcome — recorded, zero assertions, and the
          letter stays attachable. Never a name-matched guess. */}
      <button
        type="button"
        className="hm-nsc-opt alt"
        disabled={busy}
        data-testid="hm-nsc-notonfile"
        onClick={() => setChosen({ supplier_id: null, supplier_name: null })}
      >
        <span className="hm-nsc-name">{PICKER_COPY.NOT_ON_FILE_NAME}</span>
        <span className="hm-nsc-hint">{PICKER_COPY.NOT_ON_FILE_HINT}</span>
      </button>
    </div>
  );
}
